import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, CheckSquare, Inbox, Clock, Users, FileCheck, Settings, X,
  ClipboardList, Receipt, AlertOctagon, Target, Truck, GraduationCap, Grid3x3, FileText,
  ChevronDown, ChevronRight, Building2, Home, Boxes, Briefcase, LifeBuoy as LifeBuoyIcon, ShieldCheck, Smile, Wrench, AlertTriangle, ClipboardCheck, ListTodo, Scale,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { getTenantScope, setActiveAccountEmail, getActiveAccountEmail, endpoints } from "@/lib/api";

const overview = [
  { id: "dashboard", label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
];

const internalGroup = {
  id: "internal", label: "ISOQuest Workspace", icon: Briefcase,
  items: [
    { id: "calendar", label: "Calendar & Diary", to: "/calendar", icon: Calendar },
    { id: "tasks", label: "Tasks", to: "/tasks", icon: CheckSquare },
    { id: "employee-requests", label: "Employee Requests", to: "/employee-requests", icon: ClipboardList },
    { id: "assistance", label: "Assistance Requests", to: "/assistance", icon: LifeBuoyIcon },
    { id: "billing", label: "Client Billing", to: "/billing", icon: Receipt },
    { id: "clients", label: "Clients", to: "/clients", icon: Users },
  ],
};

const clientModulesGroup = {
  id: "iso", label: "ISO Modules", icon: Boxes,
  items: [
    { id: "assistance", label: "Assistance Requests", to: "/assistance", icon: LifeBuoyIcon },
    { id: "ncrs", label: "NCRs", to: "/ncrs", icon: AlertOctagon },
    { id: "objectives", label: "Strategic Objectives", to: "/objectives", icon: Target },
    { id: "suppliers", label: "Suppliers", to: "/suppliers", icon: Truck },
    { id: "employees", label: "Employees", to: "/employees", icon: Users },
    { id: "trainings", label: "Trainings", to: "/trainings", icon: GraduationCap },
    { id: "matrix", label: "Competency Matrix", to: "/competency-matrix", icon: Grid3x3 },
    { id: "documents", label: "Documents & Procedures", to: "/documents", icon: FileText },
    { id: "satisfaction", label: "Customer Satisfaction", to: "/customer-satisfaction", icon: Smile },
    { id: "equipment", label: "Equipment & Calibration", to: "/equipment", icon: Wrench },
    { id: "risks", label: "Risks & Opportunities", to: "/risks", icon: AlertTriangle },
    { id: "audits-reviews", label: "Audits & Reviews", to: "/audits-reviews", icon: ClipboardCheck },
    { id: "iso-tasks", label: "ISO Task List", to: "/iso-tasks", icon: ListTodo },
    { id: "legal-requirements", label: "Legal Requirements", to: "/legal-requirements", icon: Scale },
  ],
};

const adminGroup = {
  id: "admin", label: "Admin", icon: Settings,
  items: [
    { id: "accounts", label: "Accounts", to: "/accounts", icon: Users, badge: "New" },
    { id: "subscriptions", label: "Client Subscriptions", to: "/subscriptions", icon: Receipt, badge: "New" },
    { id: "auditor-access", label: "Auditor Access", to: "/auditor-access", icon: ShieldCheck },
    { id: "audits", label: "ISO Audits", to: "/audits", icon: FileCheck, badge: "Soon" },
    { id: "settings", label: "Settings", to: "/settings", icon: Settings },
  ],
};

const NavItem = ({ item, collapsed, onNavigate }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      data-testid={`sidebar-nav-${item.id}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all
         ${isActive
            ? "text-white shadow-md shadow-indigo-500/25 brand-gradient"
            : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
         }`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-white/20 text-white border-white/30 group-[.text-slate-600]:bg-indigo-50 group-[.text-slate-600]:text-indigo-600 group-[.text-slate-600]:border-indigo-100">
              {item.badge}
            </Badge>
          )}
        </>
      )}
    </NavLink>
  );
};

const CollapsibleGroup = ({ group, collapsed, defaultOpen, onNavigate }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = group.icon;
  if (collapsed) {
    // In compact mode, show items directly as icons
    return (
      <nav className="flex flex-col gap-1">
        {group.items.map((it) => (
          <NavItem key={it.id} item={it} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </nav>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        data-testid={`sidebar-group-${group.id}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="group flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
      >
        <Icon className="h-3.5 w-3.5 opacity-70" />
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <nav className="flex flex-col gap-0.5 pl-1">
          {group.items.map((it) => (
            <NavItem key={it.id} item={it} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </nav>
      )}
    </div>
  );
};

const NavInner = ({ collapsed, onNavigate }) => {
  const [scope, setScope] = useState(getTenantScope());
  const [permissions, setPermissions] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const onChange = () => setScope(getTenantScope());
    window.addEventListener("isoquest:scope-changed", onChange);
    const onAcc = () => endpoints.myPermissions().then(setPermissions).catch(() => setPermissions(null));
    window.addEventListener("isoquest:account-changed", onAcc);
    endpoints.myPermissions().then(setPermissions).catch(() => setPermissions(null));
    return () => {
      window.removeEventListener("isoquest:scope-changed", onChange);
      window.removeEventListener("isoquest:account-changed", onAcc);
    };
  }, []);
  const isClient = scope?.role === "client";
  const allow = permissions?.allowed_modules;
  const filterItems = (items) => allow ? items.filter((it) => allow.includes(it.id)) : items;

  const filteredInternal = { ...internalGroup, items: filterItems(internalGroup.items) };
  const filteredModules = { ...clientModulesGroup, items: filterItems(clientModulesGroup.items) };
  const filteredAdmin = { ...adminGroup, items: filterItems(adminGroup.items) };
  const showOverview = !allow || allow.includes("dashboard");

  return (
    <>
      <div className="px-3 py-3 flex-1 overflow-y-auto">
        {isClient && !collapsed && (
          <button
            data-testid="sidebar-my-dashboard"
            onClick={() => { navigate(`/clients/${scope.slug}`); onNavigate?.(); }}
            className="w-full mb-4 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 brand-gradient"
          >
            <Home className="h-4 w-4" />
            My Company Dashboard
          </button>
        )}

        {/* Overview (always shown) */}
        {showOverview && (
          <nav className="flex flex-col gap-1 mb-3">
            {overview.map((it) => (
              <NavItem key={it.id} item={it} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </nav>
        )}

        {/* Internal ISOQuest workspace — hide from clients */}
        {!isClient && filteredInternal.items.length > 0 && (
          <div className="mb-3">
            <CollapsibleGroup group={filteredInternal} collapsed={collapsed} defaultOpen onNavigate={onNavigate} />
          </div>
        )}

        {/* ISO Modules — collapsed by default per requirement */}
        {filteredModules.items.length > 0 && (
          <div className="mb-3">
            <CollapsibleGroup group={filteredModules} collapsed={collapsed} defaultOpen={false} onNavigate={onNavigate} />
          </div>
        )}

        {/* Admin */}
        {!isClient && filteredAdmin.items.length > 0 && (
          <div className="mb-3">
            <CollapsibleGroup group={filteredAdmin} collapsed={collapsed} defaultOpen onNavigate={onNavigate} />
          </div>
        )}
        {isClient && (
          <nav className="flex flex-col gap-1">
            <NavItem item={{ id: "settings", label: "Settings", to: "/settings", icon: Settings }} collapsed={collapsed} onNavigate={onNavigate} />
          </nav>
        )}
      </div>
    </>
  );
};

export default function SideNav({ collapsed, mobileOpen, onCloseMobile }) {
  return (
    <>
      {/* Desktop */}
      <aside
        data-testid="sidebar"
        className={`hidden md:flex fixed left-0 top-16 bottom-0 flex-col glass-card border-r border-slate-200/60 dark:border-white/10 transition-[width] duration-300 ease-out
        ${collapsed ? "w-20" : "w-64"}`}
      >
        <NavInner collapsed={collapsed} />
      </aside>

      {/* Mobile */}
      <Sheet open={mobileOpen} onOpenChange={(o) => !o && onCloseMobile()}>
        <SheetContent side="left" className="p-0 w-72" data-testid="mobile-sidebar">
          <div className="h-16 flex items-center justify-between px-4 border-b">
            <Logo />
            <Button variant="ghost" size="icon" onClick={onCloseMobile}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col h-[calc(100vh-64px)]">
            <NavInner collapsed={false} onNavigate={onCloseMobile} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
